import { getMemberInfo } from "@/hooks/useMembers";
import styles from "./SubscriptionDetails.module.css";
import AppStoreBadges from "@/components/ui/atoms/AppStoreBadges";
import {
  IconCreditCard,
  IconChevronDown,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MBadge from "@/components/ui/atoms/MBadge";
import MHeader from "@/components/ui/organisms/MHeader";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDocumentDateShort } from "@/utils/date-utils";
import {
  calculateDaysInfo,
  handleFaqToggle,
  handleFaqItemToggle,
} from "@/utils/subscription-utils";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MDialog from "@/components/ui/atoms/MDialog";
import { useTranslation } from "react-i18next";
import { useSubscription } from "@/hooks/useSubscription";
import { MAccordionHeader } from "@/components/ui/atoms/MAccordionHeader";
import { useIsDesktopPWA } from "@/hooks/useIsDesktopPWA";

const SubscriptionDetails = () => {
  const { t } = useTranslation();
  const isDesktopPWA = useIsDesktopPWA();
  const memberInfo = getMemberInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription, loading, fetchCurrentSubscription } = useSubscription();
  const [showCancelInfoDrawer, setShowCancelInfoDrawer] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showGraceInfoDrawer, setShowGraceInfoDrawer] = useState(false);
  const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false);
  const [isFaqOpen, setIsFaqOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const isCorporate = subscription?.subscriptionSource === "corporate";
  const isTrial = subscription?.plan?.type?.toLowerCase() === "trial";
  const trialEnded = subscription?.status.toLocaleLowerCase() === "expired";
  const planName = isCorporate
    ? t("subscription.subscriptionDetails.corporatePlanName")
    : subscription?.plan?.name || "N/A";
  const daysInfo = isTrial ? calculateDaysInfo(subscription) : null;
  const isMonthly = subscription?.plan?.type?.toLowerCase() === "monthly";
  const isCancelled = subscription?.status.toLowerCase() === "cancelled";
  const isGrace = subscription?.status.toLowerCase() === "grace";
  const isInActive = subscription?.status.toLowerCase() === "inactive";

  // Check if subscription is past renewal date
  const isPastRenewalDate = subscription?.endDate
    ? new Date(subscription.endDate) <= new Date()
    : false;
  const isExpired = isPastRenewalDate;

  // Check if plan change is scheduled (but not if subscription is cancelled)
  const hasPlanChange =
    !!subscription?.nextPlanId &&
    subscription?.status?.toLowerCase() !== "cancelled";

  // Map status for display
  const getDisplayStatus = (status: string) => {
    if (hasPlanChange) return "Plan Change Scheduled";
    if (
      isCorporate &&
      (status.toLowerCase() === "grace" || status.toLowerCase() === "expired")
    ) {
      return "Expired";
    }
    if (status.toLowerCase() === "grace") {
      return t("subscription.subscriptionDetails.status.paymentFailed");
    }
    return status;
  };

  // Get badge variant based on status
  const getBadgeVariant = () => {
    if (hasPlanChange) return "blue";
    if (isGrace) return "red";
    if (isCancelled) return "grey";
    if (isInActive) return "red";
    if (trialEnded) return "red";
    return "green";
  };

  useEffect(() => {
    fetchCurrentSubscription();
  }, [fetchCurrentSubscription]);

  useEffect(() => {
    if (location.state?.showCancelSuccess) {
      setShowCancelSuccessModal(true);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  if (loading) {
    return null;
  }

  if (!subscription) {
    return null;
  }

  const handleDeleteAccount = () => {
    // Match family index.tsx logic for FO self-delete
    const isSelf = true;
    const isFO = memberInfo?.family_member_type === "fo";
    const eligibleForOwnership: any[] = [];

    // TODO: Fetch family members if needed for ownership transfer
    // For now, navigate with basic state
    navigate("/profile/family/delete-member", {
      state: {
        action: "fo-delete-registered",
        targetMember: memberInfo,
        eligibleForOwnership,
      },
    });
    setShowDeleteAccountModal(false);
  };

  return (
    <div className="profile-page-root">
      <MHeader
        title={t("subscription.subscriptionDetails.title")}
        module="subscription"
        memberInfo={memberInfo}
        description={t("subscription.subscriptionDetails.description")}
        showGradient
      />

      <MSpacer mt={32} />

      <div className={styles.subscriptionDetails}>
        {/* Current Plan Section */}
        <div className={styles.sectionTitle}>
          {t("subscription.subscriptionDetails.currentPlan")}
        </div>

        <div className={styles.planCard}>
          <div className={styles.planHeader}>
            <div className={styles.planIconWrapper}>
              <IconCreditCard size={24} color="#7046AE" />
            </div>
            <div className={styles.planInfo}>
              <div className={styles.planName}>{planName}</div>
              {isCorporate && subscription.params?.orgName && (
                <div className={styles.planOrgName}>
                  {subscription.params.orgName}
                </div>
              )}
            </div>
            <div className={styles.planStatus}>
              <MBadge variant={getBadgeVariant()}>
                {getDisplayStatus(subscription.status)}
              </MBadge>
            </div>
          </div>
          {isTrial && !trialEnded && daysInfo && (
            <div className={styles.planDays}>
              {t("subscription.subscriptionDetails.dayOfTrial", {
                current: daysInfo.current,
                total: daysInfo.total,
              })}
            </div>
          )}
          {trialEnded && isTrial && (
            <div className={styles.planDays}>
              {t("subscription.subscriptionDetails.trialEnded")}
            </div>
          )}
          <div className={styles.divider} />

          <div className={styles.planDetails}>
            {isCorporate ? (
              isGrace ? (
                <>
                  {/* Expired on — date of offboarding stored in cancelledOn */}
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabelGrey}>
                      {t("subscription.subscriptionDetails.expiredOn")}
                    </span>
                    <span className={styles.detailValueGrey}>
                      {formatDocumentDateShort(
                        subscription.cancelledOn ?? subscription.endDate,
                        true
                      )}
                    </span>
                  </div>
                  {/* Grace ends on */}
                  <div className={styles.detailRow}>
                    <div className={styles.detailLabelWithIcon}>
                      <span className={styles.detailLabel}>
                        {t("subscription.subscriptionDetails.graceEndsOn")}
                      </span>
                      <div
                        style={{
                          background: "#edf3fc",
                          borderRadius: "24px",
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                        onClick={() => setShowGraceInfoDrawer(true)}
                      >
                        <IconInfoCircle size={18} color="#5B8DEF" />
                      </div>
                    </div>
                    <span className={styles.detailValue}>
                      {formatDocumentDateShort(subscription.endDate, true)}
                    </span>
                  </div>
                </>
              ) : (
                /* Active corporate — just Valid till */
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>
                    {t("subscription.subscriptionDetails.validTill")}
                  </span>
                  <span className={styles.detailValue}>
                    {formatDocumentDateShort(subscription.endDate, true)}
                  </span>
                </div>
              )
            ) : (
              <>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>
                    {t("subscription.subscriptionDetails.amount")}
                  </span>
                  <span className={styles.detailValue}>
                    {(subscription.currency || "INR") === "INR"
                      ? "₹"
                      : subscription.currency || "INR"}{" "}
                    {subscription.amount ||
                      subscription.plan?.products?.[0]?.price ||
                      0}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <div className={styles.detailLabelWithIcon}>
                    <span
                      className={
                        isExpired || isGrace
                          ? styles.detailLabelGrey
                          : styles.detailLabel
                      }
                    >
                      {isCancelled
                        ? t("subscription.subscriptionDetails.activeUntil")
                        : isTrial
                        ? t("subscription.subscriptionDetails.trialEndDate")
                        : t("subscription.subscriptionDetails.renewsOn")}
                    </span>
                    {isCancelled && !isExpired && (
                      <IconInfoCircle
                        size={16}
                        color="var(--colour-icon-info, #5B8DEF)"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowCancelInfoDrawer(true)}
                      />
                    )}
                  </div>
                  <span
                    className={
                      isExpired || isGrace
                        ? styles.detailValueGrey
                        : styles.detailValue
                    }
                  >
                    {formatDocumentDateShort(subscription.endDate, true)}
                  </span>
                </div>
                {hasPlanChange && subscription.nextPlanId && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      Plan will change to
                    </span>
                    <span className={styles.detailValue}>
                      Plan {subscription.nextPlanId}
                    </span>
                  </div>
                )}
                {isGrace && (
                  <div className={styles.detailRow}>
                    <div className={styles.detailLabelWithIcon}>
                      <span className={styles.detailLabel}>
                        {t("subscription.subscriptionDetails.graceEndsOn")}
                      </span>
                      <IconInfoCircle
                        size={16}
                        color="var(--colour-icon-info, #5B8DEF)"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowGraceInfoDrawer(true)}
                      />
                    </div>
                    <span className={styles.detailValue}>
                      {formatDocumentDateShort(subscription.endDate, true)}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {isCorporate && isGrace && (
          <>
            <MSpacer mt={32} />
            <p className={styles.corporateMobileText}>
              {t("subscription.subscriptionDetails.corporateMobileAppCtaLine1")}
              <br />
              {t("subscription.subscriptionDetails.corporateMobileAppCtaLine2")}
            </p>
            <MSpacer mt={24} />
            <AppStoreBadges />
          </>
        )}

        {!isCorporate && (
          <>
            <MSpacer mt={24} />

            <button
              className={styles.linkButton}
              onClick={() => navigate("/profile/subscription/purchase-history")}
            >
              {t("subscription.subscriptionDetails.viewPurchaseHistory")}
            </button>

            <MSpacer mt={24} />

            <div className={styles.faqSection}>
              <div className={styles.faqHeaderWrapper}>
                <MAccordionHeader
                  title={t("subscription.subscriptionDetails.faqs")}
                  isOpen={isFaqOpen}
                  onClick={() =>
                    handleFaqToggle(isFaqOpen, setIsFaqOpen, setOpenFaqIndex)
                  }
                  className={styles.faqAccordionHeader}
                  titleColor="var(--brand-colours-indigo-indigo-200, #AD95D1)"
                />
              </div>

              {isFaqOpen && (
                <div className={styles.faqContainer}>
                  {[0, 1, 2].map((index) => (
                    <div key={index} className={styles.faqItem}>
                      <MAccordionHeader
                        title={t(
                          `subscription.subscriptionDetails.faq.${index}.question`
                        )}
                        isOpen={openFaqIndex === index}
                        onClick={() =>
                          handleFaqItemToggle(
                            index,
                            openFaqIndex,
                            setOpenFaqIndex
                          )
                        }
                        titleColor="#36116D"
                      />
                      {openFaqIndex === index && (
                        <div className={styles.faqAnswer}>
                          {t(
                            `subscription.subscriptionDetails.faq.${index}.answer`
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <MSpacer mt={16} />

            {/* Contact Support Link */}
            <div style={{ textAlign: "center" }}>
              <MButton
                variant="link"
                text="Contact support"
                size="md"
                onClick={() => {
                  window.location.href = "mailto:support@maatra.health";
                }}
              />
            </div>
          </>
        )}
      </div>

      {!isDesktopPWA && (
        <div className={styles.actionButtons}>
          {isGrace ? (
            <>
              {!isCorporate && (
                <MButton
                  className={styles.subscribeButton}
                  text={t("subscription.subscriptionDetails.retryPayment")}
                  size="lg"
                  onClick={() => {
                    /*retry payment */
                  }}
                />
              )}
              <MButton
                variant={isCorporate ? undefined : "outline"}
                className={isCorporate ? styles.subscribeButton : undefined}
                text={t("subscription.subscriptionDetails.manageSubscription")}
                size="lg"
                onClick={() => navigate("/profile/subscription/manage")}
              />
            </>
          ) : (
            (!isCorporate || isExpired) && (
              <>
                <MButton
                  className={styles.subscribeButton}
                  text={
                    isTrial
                      ? t("subscription.subscriptionDetails.subscribe")
                      : t("subscription.subscriptionDetails.manageSubscription")
                  }
                  size="lg"
                  onClick={() => navigate("/profile/subscription/manage")}
                />
                {isTrial && (
                  <MButton
                    variant="link"
                    text={
                      trialEnded
                        ? t("subscription.subscriptionDetails.deleteAccount")
                        : t(
                            "subscription.subscriptionDetails.cancelTrialAndDelete"
                          )
                    }
                    size="lg"
                    onClick={() => setShowDeleteAccountModal(true)}
                  />
                )}
              </>
            )
          )}
        </div>
      )}

      {/* Cancel Info Drawer */}
      <FooterDrawer
        open={showCancelInfoDrawer}
        onClose={() => setShowCancelInfoDrawer(false)}
        title="Active until"
      >
        <div className={styles.drawerContent}>
          Even though the plan is cancelled, you’ll have full access until this
          date.
        </div>
      </FooterDrawer>

      {/* Grace Period Info Drawer */}
      <FooterDrawer
        open={showGraceInfoDrawer}
        onClose={() => setShowGraceInfoDrawer(false)}
        height="20vh"
        title={
          isCorporate
            ? t(
                "subscription.subscriptionDetails.gracePeriodDrawer.corporateTitle"
              )
            : "Grace period"
        }
      >
        <div className={styles.drawerContent}>
          {isCorporate
            ? t(
                "subscription.subscriptionDetails.gracePeriodDrawer.corporateDescription"
              )
            : subscription?.plan?.type === "annual"
            ? t(
                "subscription.subscriptionDetails.gracePeriodDrawer.annualDescription"
              )
            : t(
                "subscription.subscriptionDetails.gracePeriodDrawer.monthlyDescription"
              )}
        </div>
      </FooterDrawer>

      {/* Delete Account Modal */}
      <MDialog
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        title={t("subscription.subscriptionDetails.deleteAccountModal.title")}
        description={t(
          "subscription.subscriptionDetails.deleteAccountModal.description"
        )}
        primaryCtaText={t(
          "subscription.subscriptionDetails.deleteAccountModal.confirmButton"
        )}
        secondaryCtaText={t(
          "subscription.subscriptionDetails.deleteAccountModal.cancelButton"
        )}
        onPrimaryCta={handleDeleteAccount}
        onSecondaryCta={() => setShowDeleteAccountModal(false)}
      />

      {/* Cancel Success Modal */}
      <MDialog
        isOpen={showCancelSuccessModal}
        onClose={() => setShowCancelSuccessModal(false)}
        title=""
        primaryCtaText=""
        onPrimaryCta={() => {}}
        showPrimaryCta={false}
        showCloseButton={false}
      >
        <div className={styles.cancelSuccessDialogRoot}>
          <button
            className={styles.cancelSuccessCloseBtn}
            onClick={() => setShowCancelSuccessModal(false)}
          >
            <IconX size={20} />
          </button>
          <div className={styles.cancelSuccessContent}>
            <h2 className={styles.cancelSuccessTitle}>
              {t("subscription.manageSubscription.successModal.title")}
            </h2>
            <p className={styles.cancelSuccessDescription}>
              {t("subscription.manageSubscription.successModal.description")}
            </p>
          </div>
        </div>
      </MDialog>
    </div>
  );
};

export default SubscriptionDetails;
